'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { api, upload } from './api';

/** What `api` is: the one function every read and write goes through. */
export type ApiFn = typeof api;

/** Files go the other way round: a form, not JSON, so they have a function of their own. */
export type UploadFn = typeof upload;

interface Transport {
  api: ApiFn;
  upload: UploadFn;
  /** False where nothing can push: a preview has no server to open a stream to. */
  streaming: boolean;
  /** Without a stream, how often a live picture is read again; 0 reads it once. */
  pollMs: number;
}

const REAL: Transport = { api, upload, streaming: true, pollMs: 0 };
const ApiContext = createContext<Transport>(REAL);

/**
 * Which `api` a subtree talks through: the real one unless a preview sets
 * another. A hook, not a module variable: a variable is process-wide, and the
 * admin's own requests would be answered with a client's fixtures meanwhile.
 */
export function ApiProvider({
  value,
  uploader = upload,
  streaming = true,
  pollMs = 0,
  children,
}: {
  value: ApiFn;
  uploader?: UploadFn;
  streaming?: boolean;
  pollMs?: number;
  children: ReactNode;
}) {
  const transport = useMemo(() => ({ api: value, upload: uploader, streaming, pollMs }), [value, uploader, streaming, pollMs]);
  return <ApiContext.Provider value={transport}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiFn {
  return useContext(ApiContext).api;
}

export function useUpload(): UploadFn {
  return useContext(ApiContext).upload;
}

/** Whether to open an EventSource at all; a preview answers reads and never pushes. */
export function useStreaming(): boolean {
  return useContext(ApiContext).streaming;
}

export function usePollMs(): number {
  return useContext(ApiContext).pollMs;
}
